import { computed, inject, Injectable, signal } from '@angular/core';
import { AgentClient } from './agent-client';

export interface ChatMessage {
    role: 'user' | 'assistant';
    text: string;
}

/**
 * One conversation with the agent: the messages shown, the turn in flight and the id of the
 * last completed response, which the next turn continues from. A failed turn leaves that id
 * as it was, so the conversation resumes from the last answer that completed.
 */
@Injectable({ providedIn: 'root' })
export class Conversation {
    private readonly client = inject(AgentClient);
    private readonly state = signal<{ messages: ChatMessage[]; error: string | null; busy: boolean }>({
        messages: [],
        error: null,
        busy: false
    });
    private lastResponseId: string | undefined;

    readonly messages = computed(() => this.state().messages);
    readonly error = computed(() => this.state().error);
    readonly busy = computed(() => this.state().busy);

    async send(input: string): Promise<void> {
        const text = input.trim();
        if (text === '' || this.state().busy) {
            return;
        }

        this.state.update(s => ({
            messages: [...s.messages, { role: 'user', text }],
            error: null,
            busy: true
        }));
        let answer = '';
        let error: string | null = null;

        for await (const event of this.client.send(text, this.lastResponseId)) {
            switch (event.type) {
                case 'text':
                    answer += event.text;
                    this.showAnswer(answer);
                    break;
                case 'completed':
                    this.lastResponseId = event.responseId;
                    break;
                case 'error':
                    error = event.message;
                    break;
            }
        }

        this.state.update(s => ({ messages: s.messages, error, busy: false }));
    }

    reset(): void {
        this.lastResponseId = undefined;
        this.state.set({ messages: [], error: null, busy: false });
    }

    /** Replaces the assistant message of the turn in flight, adding it on the first text. */
    private showAnswer(text: string): void {
        this.state.update(s => {
            const last = s.messages[s.messages.length - 1];
            const messages =
                last?.role === 'assistant'
                    ? [...s.messages.slice(0, -1), { role: 'assistant' as const, text }]
                    : [...s.messages, { role: 'assistant' as const, text }];
            return { ...s, messages };
        });
    }
}
